import type { BuiltinBlockRegistryDefinition, builtinBlocks } from 'virtual:monospace-builtin-blocks';
import type { Component, DefineComponent, FunctionalComponent, RenderFunction, VNode } from 'vue';
import type { ComponentProps, ComponentSlots } from 'vue-component-type-helpers';
import type { DeepGet, DeepKeys } from '#layers/base/public';
import type { BlockDefinition, BlockSerialized } from '#layers/block-engine/types/block';
import type { TranslationPlaceholder } from '#layers/i18n/public';
import type { FormFieldProps, FormFieldSlots } from '#layers/ui/public';

type SlotFn = RenderFunction | ((slotProps: Record<string, unknown>) => VNode | VNode[]);

/* Base */

export const FormSchemaKind = {
	Presentational: 'presentational',
	Section: 'section',
	Sections: 'sections',
} as const;

export type FormSchemaKind = typeof FormSchemaKind[keyof typeof FormSchemaKind];

type FormSchemaFieldName<TValues extends object> = [DeepKeys<TValues> & string] extends [never] ? string : DeepKeys<TValues> & string;

/**
 * Infer the field value when `name` is a literal deep key. Dynamic names fall
 * back to unknown so adapter helpers do not leak unsafe top types into editor hovers.
 */
export type FormSchemaFieldValue<TValues extends object, TName> = TName extends string
	? string extends TName
		? unknown
		: DeepGet<TValues, TName>
	: unknown;

interface FormSchemaFieldBase<
	TResult extends object,
	TName = FormSchemaFieldName<TResult>,
> {
	name: TName;
	label?: TranslationPlaceholder;
	description?: TranslationPlaceholder;
	required?: boolean;
	/**
	 * Whether `null` is a valid value for this field, i.e. it can be cleared to
	 * empty. Distinct from `required` (a submit-time validation constraint):
	 * a field can be required yet clearable, or optional yet non-nullable.
	 * Input blocks that support a clear affordance derive it from this.
	 *
	 * @default false
	 */
	nullable?: boolean;
	hidden?: boolean | (() => boolean);
	icon?: string;

	default?: TName extends string ? () => FormSchemaFieldValue<TResult, TName> : never;

	/**
	 * Also fall back to `default` when the loaded value is `null`, not just `undefined`.
	 * Use for settings-style enums where `null` means "unset", not "user cleared it".
	 */
	defaultOnNull?: boolean;

	/**
	 * Don't wrap this field in a form field component
	 */
	unwrapField?: boolean;
	fieldProps?: Omit<FormFieldProps, 'name' | 'label' | 'description' | 'required' | 'class' | 'ui'>;
	fieldSlots?: FormFieldSlots;

	/**
	 * Data type of the prop. Used by the block editor for widget selection
	 * and expression evaluation. When omitted, the type is inferred from
	 * Vue's runtime prop definitions.
	 */
	type?: 'string' | 'number' | 'boolean' | 'enum' | 'object';
	/** Whether the prop accepts an array of values */
	array?: boolean;
	/** Allowed values for enum-typed props */
	values?: Array<string | number | boolean | null | undefined>;
	/**
	 * Mark this option as two-way bindable in block metadata.
	 * This synthesizes an `update:<name>` event for the builder contract.
	 */
	model?: boolean;
}

/* Block Props */

/**
 * Get all properties that can't be `undefined`
 */
type GetRequiredProps<T> = {
	[K in keyof T]-?: K extends 'modelValue'
	// Exclude modelValue from required props because it's implicitly bound
		? never
		: T[K] extends Exclude<T[K], undefined>
			? K
			: never;
}[keyof T];

/**
 * Transform properties with `| undefined` to optional properties
 */
type GetPropsWithOptionalProperties<TProps> = Pick<TProps, GetRequiredProps<TProps>> & Partial<TProps>;

type FilterVueProps<T> = Omit<{
	[K in keyof T as K extends `onVnode${string}` | `ref_${string}`
		? never
		: K]: T[K];
}, 'class' | 'style' | 'ref' | 'key'>;

type InferBlockProps<TBlock extends BlockDefinition> = FilterVueProps<ComponentProps<TBlock['comp']>>;

/* Slots */

type InferBlockSlots<TBlock extends BlockDefinition> = ComponentSlots<TBlock['comp']>;

/* Builtin blocks */

type FormSchemaFieldBlockBuiltinValue<
	TBlock extends BuiltinBlockRegistryDefinition,
	TProps extends InferBlockProps<TBlock> = InferBlockProps<TBlock>,
> = (GetRequiredProps<TProps> extends never ? {
	// Optional because only contains optional props
	props?: GetPropsWithOptionalProperties<TProps>;
} : {
	// Required because contains some required props
	props: GetPropsWithOptionalProperties<TProps>;
}) & {
	block: TBlock['id'];
	slots?: Partial<InferBlockSlots<TBlock>>;
};

interface FormSchemaFieldBlockBuiltinUnknownIdValue {
	block: string;
	props?: Record<string, unknown>;
	slots?: Record<string, SlotFn>;
}

/**
 * We need a Map type to be able to create the 'union' type, so we have distinct props for each block
 */
type FormSchemaFieldBlockBuiltinValueMap = {
	[key in keyof typeof builtinBlocks]: FormSchemaFieldBlockBuiltinValue<typeof builtinBlocks[key]>;
};

export type FormSchemaFieldBlockBuiltin<
	TResult extends object,
	TName,
> = FormSchemaFieldBase<TResult, TName>
  // Equivalent to a union of all possible builtin block value types
  // `FormSchemaFieldBlockBuiltinValue<typeof builtinBlocks['$builtin:Input']> |
  // FormSchemaFieldBlockBuiltinValue<typeof builtinBlocks['$builtin:InputSelect']> | ...`
	& FormSchemaFieldBlockBuiltinValueMap[keyof FormSchemaFieldBlockBuiltinValueMap];

export type FormSchemaFieldBlockBuiltinUnknownId<
	TResult extends object,
	TName,
> = FormSchemaFieldBase<TResult, TName> & FormSchemaFieldBlockBuiltinUnknownIdValue;

/* Serialized block */

interface FormSchemaFieldBlockSerializedValue {
	block: BlockSerialized;
	props?: Record<string, unknown>;
	slots?: Record<string, SlotFn>;
}

export type FormSchemaFieldBlockSerialized<
	TResult extends object,
	TName,
> = FormSchemaFieldBase<TResult, TName> & FormSchemaFieldBlockSerializedValue;

/* Render Function */

export type FormSchemaFieldRender<
	TResult extends object,
	TName,
> = FormSchemaFieldBase<TResult, TName> & {
	render: FunctionalComponent | Component | DefineComponent;
};

/* Custom render field (no form binding) */

export interface FormSchemaFieldCustomRender {
	name: string;
	render: FunctionalComponent | Component | DefineComponent;
	unwrapField?: boolean;
	label?: TranslationPlaceholder;
	description?: TranslationPlaceholder;
	hidden?: boolean | (() => boolean);
}

/* Structural entries */

/** Blocks that can be rendered directly by FormSchema presentational entries. */
type FormSchemaPresentationalBlock = string | BlockSerialized | BlockDefinition;

/** Blocks whose FormSchema chrome declaration can be resolved before rendering nested fields. */
type FormSchemaChromeDeclarationBlock = string | BlockDefinition;

interface FormSchemaStructuralEntryBase<TBlock> {
	/** Block used to render this structural entry. */
	block: TBlock;
	/** Props forwarded to the block. */
	props?: Record<string, unknown>;
	/** Slots forwarded to the block. */
	slots?: Record<string, SlotFn>;
	/** Hide this structural entry from rendering. */
	hidden?: boolean | (() => boolean);
}

export interface FormSchemaPresentational extends FormSchemaStructuralEntryBase<FormSchemaPresentationalBlock> {
	/** Structural entry kind. */
	kind: typeof FormSchemaKind.Presentational;
}

export interface FormSchemaSection<TResult extends object = Record<string, unknown>> extends FormSchemaStructuralEntryBase<FormSchemaChromeDeclarationBlock> {
	/** Structural entry kind. */
	kind: typeof FormSchemaKind.Section;
	/** Nested field list rendered inside the configured chrome slot. */
	fields: FormSchemaField<TResult>[];
}

export interface FormSchemaSectionsItem<TResult extends object = Record<string, unknown>> {
	/** Props merged into the chrome item for this section. */
	props?: Record<string, unknown>;
	/** Nested field list rendered inside this section. */
	fields: FormSchemaField<TResult>[];
	/** Hide this section item from rendering. */
	hidden?: boolean | (() => boolean);
}

export interface FormSchemaSections<TResult extends object = Record<string, unknown>> extends FormSchemaStructuralEntryBase<FormSchemaChromeDeclarationBlock> {
	/** Structural entry kind. */
	kind: typeof FormSchemaKind.Sections;
	/** Nested sections rendered by the configured chrome block. */
	sections: FormSchemaSectionsItem<TResult>[];
}

export type FormSchemaStructuralEntry<TResult extends object = Record<string, unknown>> =
	| FormSchemaPresentational
	| FormSchemaSection<TResult>
	| FormSchemaSections<TResult>;

/* Form Schema */

type UnionKeys<T> = T extends T ? keyof T : never;
type StrictUnionHelper<T, TAll> = T extends unknown ? T & Partial<Record<Exclude<UnionKeys<TAll>, keyof T>, never>> : never;
type StrictUnion<T> = StrictUnionHelper<T, T>;

export type FormSchemaFieldActual<
	TResult extends object,
	TName,
> = StrictUnion<FormSchemaFieldBlockBuiltin<TResult, TName> | FormSchemaFieldBlockBuiltinUnknownId<TResult, TName> | FormSchemaFieldBlockSerialized<TResult, TName> | FormSchemaFieldRender<TResult, TName> | FormSchemaFieldBase<TResult, TName>>;

export type FormSchemaDataField<
	TResult extends object,
	TName,
> = FormSchemaFieldActual<TResult, TName> | FormSchemaFieldCustomRender;

export type FormSchemaFieldResolved<
	TResult extends object,
	TName,
> = StrictUnion<FormSchemaDataField<TResult, TName> | FormSchemaStructuralEntry<TResult>>;

export type FormSchemaEntry<
	TResult extends object = Record<string, unknown>,
	TName = FormSchemaFieldName<TResult>,
> = FormSchemaFieldResolved<TResult, TName>;

export type FormSchemaField<
	TResult extends object,
	TName = FormSchemaFieldName<TResult>,
> = TName extends string
	? FormSchemaFieldResolved<TResult, TName>
	: never;

export type FormSchemaResolved<TResult extends object = Record<string, unknown>> = FormSchemaField<TResult>[];

/* Getter */

/**
 * Symbol to mark that the form schema was created via the `form` function from the getter payload
 */
const shouldUseFormFnSymbol: unique symbol = Symbol('FormSchemaStrict');

interface FormSchemaGetterPayload<
	TResult extends object,
	TContext,
> {
	form: (schema: FormSchemaResolved<TResult>) => FormSchemaResolved<TResult> & { [shouldUseFormFnSymbol]: true };
	values: TResult;
	context: TContext;
}

export type ResolvableFormSchema<
	TResult extends object = Record<string, unknown>,
	TContext = unknown,
> = ((payload: FormSchemaGetterPayload<TResult, TContext>) => FormSchemaResolved<TResult> & { [shouldUseFormFnSymbol]: true });

export type FormSchema<
	TResult extends object = Record<string, unknown>,
	TContext = unknown,
> = FormSchemaResolved<TResult> | ResolvableFormSchema<TResult, TContext>;
